import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Building2,
  MapPin,
  Home,
  Users,
  ArrowLeft,
  ArrowRight,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Copy,
  Share2,
  KeyRound,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CreateBuilding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState('form'); // form, duplicates, success
  const [loading, setLoading] = useState(false);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [createdBuilding, setCreatedBuilding] = useState(null);
  const token = localStorage.getItem('session_token');

  const [formData, setFormData] = useState({
    name: '',
    building_type: '',
    address: '',
    city: '',
    province: '',
    country: 'South Africa',
    postal_code: '',
    total_units: '',
    description: '',
    nominated_agent: {
      name: '',
      email: '',
      company_name: '',
      phone: '',
    },
  });

  const buildingTypes = [
    { value: 'sectional_title', label: 'Sectional Title', description: 'Apartments, townhouses' },
    { value: 'hoa', label: 'HOA', description: 'Homeowners Association' },
    { value: 'residential_estate', label: 'Residential Estate', description: 'Gated community' },
    { value: 'apartment_block', label: 'Apartment Block', description: 'Flat complex' }
  ];

  const provinces = [
    'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 
    'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape'
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAgentChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      nominated_agent: { ...prev.nominated_agent, [field]: value },
    }));
  };

  const handleCheckDuplicates = async () => {
    if (!formData.name || !formData.city) {
      toast.error('Please enter building name and city first');
      return;
    }

    setCheckingDuplicates(true);
    try {
      const response = await fetch(`${API_URL}/api/building-discovery/check-duplicates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          address: formData.address,
          city: formData.city
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.has_duplicates) {
          setDuplicates(data.potential_duplicates);
          setStep('duplicates');
        } else {
          // No duplicates, proceed to create
          handleCreateBuilding();
        }
      }
    } catch (error) {
      console.error('Duplicate check error:', error);
      toast.error('Failed to check for duplicates');
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const handleCreateBuilding = async () => {
    // Validate required fields
    if (!formData.name || !formData.building_type || !formData.address || 
        !formData.city || !formData.province || !formData.total_units) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const agent = formData.nominated_agent || {};
      const payload = {
        ...formData,
        total_units: parseInt(formData.total_units),
        nominated_agent: (agent.name || agent.email || agent.company_name)
          ? {
              name: agent.name?.trim() || null,
              email: agent.email?.trim().toLowerCase() || null,
              company_name: agent.company_name?.trim() || null,
              phone: agent.phone?.trim() || null,
            }
          : null,
      };
      const response = await fetch(`${API_URL}/api/building-discovery/create-building`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setCreatedBuilding(data.building);
        toast.success('Building created!');
        // Send the user straight to the payment / trial choice page
        const hoaId = data.building?.hoa_id || data.building?.building_id;
        navigate(`/building/${hoaId}/activate`, { state: { building: data.building } });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create building');
      }
    } catch (error) {
      console.error('Create building error:', error);
      toast.error('Failed to create building');
    } finally {
      setLoading(false);
    }
  };

  const copyInviteCode = () => {
    if (createdBuilding?.invite_code) {
      navigator.clipboard.writeText(createdBuilding.invite_code);
      toast.success('Invite code copied!');
    }
  };

  const copyInviteLink = () => {
    if (createdBuilding?.invite_link) {
      navigator.clipboard.writeText(createdBuilding.invite_link);
      toast.success('Invite link copied!');
    }
  };

  // Form Step
  if (step === 'form') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 p-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <Button
            variant="ghost"
            className="text-white mb-4"
            onClick={() => navigate('/building-onboarding')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <Card className="bg-white shadow-xl">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <CardTitle>Create New Building</CardTitle>
                  <CardDescription>Register your building on BodyCorp365</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Building Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Building Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Ocean View Apartments"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  data-testid="building-name-input"
                />
              </div>

              {/* Building Type */}
              <div className="space-y-2">
                <Label>Building Type *</Label>
                <Select 
                  value={formData.building_type} 
                  onValueChange={(value) => handleChange('building_type', value)}
                >
                  <SelectTrigger data-testid="building-type-select">
                    <SelectValue placeholder="Select building type" />
                  </SelectTrigger>
                  <SelectContent>
                    {buildingTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div>
                          <span className="font-medium">{type.label}</span>
                          <span className="text-slate-500 text-sm ml-2">({type.description})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label htmlFor="address">Street Address *</Label>
                <Input
                  id="address"
                  placeholder="e.g., 123 Beach Road"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  data-testid="building-address-input"
                />
              </div>

              {/* City & Province Row */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    placeholder="e.g., Cape Town"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    data-testid="building-city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Province *</Label>
                  <Select 
                    value={formData.province} 
                    onValueChange={(value) => handleChange('province', value)}
                  >
                    <SelectTrigger data-testid="building-province-select">
                      <SelectValue placeholder="Select province" />
                    </SelectTrigger>
                    <SelectContent>
                      {provinces.map((province) => (
                        <SelectItem key={province} value={province}>
                          {province}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Country & Postal Code Row */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                    data-testid="building-country-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postal_code">Postal Code</Label>
                  <Input
                    id="postal_code"
                    placeholder="e.g., 8001"
                    value={formData.postal_code}
                    onChange={(e) => handleChange('postal_code', e.target.value)}
                    data-testid="building-postal-input"
                  />
                </div>
              </div>

              {/* Number of Units */}
              <div className="space-y-2">
                <Label htmlFor="total_units">Number of Units *</Label>
                <Input
                  id="total_units"
                  type="number"
                  min="1"
                  max="1000"
                  placeholder="e.g., 24"
                  value={formData.total_units}
                  onChange={(e) => handleChange('total_units', e.target.value)}
                  data-testid="building-units-input"
                />
                <p className="text-xs text-slate-500">Total number of units/apartments in the building</p>
              </div>

              {/* Description (Optional) */}
              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of the building..."
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={3}
                  data-testid="building-description-input"
                />
              </div>

              {/* Nominated Managing Agent */}
              <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-5 space-y-4">
                <div>
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Users className="w-4 h-4 text-indigo-600" />
                    Nominate your Managing Agent
                  </h3>
                  <p className="text-xs text-slate-600 mt-1">
                    We&apos;ll email your agent so they can approve your account. Until they do, your
                    account is <strong>read-only</strong> — you can browse but not log tickets, upload
                    documents, post in chat, publish notices or vote.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="agent_name" className="text-xs">Agent Name</Label>
                    <Input
                      id="agent_name"
                      placeholder="e.g., Jane Doe"
                      value={formData.nominated_agent.name}
                      onChange={(e) => handleAgentChange('name', e.target.value)}
                      data-testid="nominated-agent-name-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="agent_email" className="text-xs">Agent Email</Label>
                    <Input
                      id="agent_email"
                      type="email"
                      placeholder="agent@company.co.za"
                      value={formData.nominated_agent.email}
                      onChange={(e) => handleAgentChange('email', e.target.value)}
                      data-testid="nominated-agent-email-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="agent_company" className="text-xs">Company Name</Label>
                    <Input
                      id="agent_company"
                      placeholder="e.g., XYZ Property Management"
                      value={formData.nominated_agent.company_name}
                      onChange={(e) => handleAgentChange('company_name', e.target.value)}
                      data-testid="nominated-agent-company-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="agent_phone" className="text-xs">Agent Phone (optional)</Label>
                    <Input
                      id="agent_phone"
                      placeholder="e.g., +27 82 000 0000"
                      value={formData.nominated_agent.phone}
                      onChange={(e) => handleAgentChange('phone', e.target.value)}
                      data-testid="nominated-agent-phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-900">You'll become the Primary Building Admin</p>
                    <p className="text-blue-700 mt-1">
                      As the creator, you'll have full permissions to invite trustees, 
                      residents, and manage all building settings.
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                className="w-full h-12"
                onClick={handleCheckDuplicates}
                disabled={loading || checkingDuplicates || !formData.name || !formData.building_type || !formData.address || !formData.city || !formData.province || !formData.total_units}
                data-testid="create-building-btn"
              >
                {checkingDuplicates ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Checking for duplicates...
                  </>
                ) : loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating building...
                  </>
                ) : (
                  <>
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Create Building
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Duplicates Warning Step
  if (step === 'duplicates') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-white shadow-xl">
          <CardHeader className="text-center">
            <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-2">
              <AlertTriangle className="w-7 h-7 text-amber-600" />
            </div>
            <CardTitle>Possible Existing Building Found</CardTitle>
            <CardDescription>
              We found similar buildings. Would you like to join one of these instead?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {duplicates.map((building) => (
              <Card key={building.building_id} className="border">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold">{building.name}</h4>
                      <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {building.city}, {building.province}
                      </p>
                      {building.total_units && (
                        <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                          <Users className="w-3 h-3" />
                          {building.total_units} units
                        </p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/building-onboarding?join=${building.building_id}`)}
                    >
                      Join This
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}

            <div className="pt-4 border-t space-y-2">
              <Button
                className="w-full"
                variant="outline"
                onClick={() => {
                  setDuplicates([]);
                  handleCreateBuilding();
                }}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <>
                    <Building2 className="w-4 h-4 mr-2" />
                    Create New Building Anyway
                  </>
                )}
              </Button>
              <Button
                className="w-full"
                variant="ghost"
                onClick={() => setStep('form')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back and Edit
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success Step
  if (step === 'success' && createdBuilding) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-white shadow-xl">
          <CardContent className="pt-8 pb-6 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Building Created!</h2>
            <p className="text-slate-600 mb-6">
              <strong>{createdBuilding.name}</strong> has been successfully registered.
            </p>

            {/* Building Info */}
            <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-center gap-2 text-slate-700 mb-2">
                <MapPin className="w-4 h-4" />
                <span>{createdBuilding.address}, {createdBuilding.city}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <Users className="w-4 h-4" />
                <span>{createdBuilding.total_units} units</span>
              </div>
            </div>

            {/* Invite Code */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <KeyRound className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-900">Building Invite Code</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <code className="text-2xl font-mono font-bold text-blue-700 bg-white px-4 py-2 rounded">
                  {createdBuilding.invite_code}
                </code>
                <Button size="sm" variant="ghost" onClick={copyInviteCode}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-blue-600 mt-2">
                Share this code with residents to let them join instantly
              </p>
            </div>

            {/* Share Link */}
            <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg mb-6">
              <input 
                type="text" 
                value={createdBuilding.invite_link} 
                readOnly 
                className="flex-1 bg-transparent text-sm text-slate-600 outline-none"
              />
              <Button size="sm" variant="ghost" onClick={copyInviteLink}>
                <Share2 className="w-4 h-4" />
              </Button>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <Button
                className="w-full"
                onClick={() => navigate('/trustee-hub')}
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/trustee-hub/residents')}
              >
                <Users className="w-4 h-4 mr-2" />
                Invite Residents
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
